import { useEffect, useState } from 'react';
import {
  CAMPAIGN_MESSAGE_TEMPLATE_LIMITS,
  type CampaignChannelKind,
  type CampaignTestSendPreviewResult,
} from '@workspace/types';
import { contactAgentApi } from '../api/contactAgentApi';
import { RenderedEmailPreviewFrame } from './RenderedEmailPreviewFrame';

const PREVIEW_DEBOUNCE_MS = 400;

export function MessageTemplatePreview({
  channelKind,
  subject,
  bodyText,
  bodyHtml,
}: {
  channelKind: CampaignChannelKind;
  subject?: string;
  bodyText: string;
  bodyHtml?: string;
}) {
  const [preview, setPreview] = useState<CampaignTestSendPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (channelKind === 'WHATSAPP') {
      setPreview(null);
      return;
    }
    const trimmed = bodyText.trim();
    if (!trimmed) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      void contactAgentApi
        .previewMessageTemplate({
          channelKind,
          subject: subject?.trim() || undefined,
          bodyText: trimmed,
          bodyHtml: bodyHtml?.trim() || undefined,
        })
        .then(result => {
          if (!cancelled) {
            setPreview(result);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPreview(null);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [bodyHtml, bodyText, channelKind, subject]);

  if (channelKind === 'WHATSAPP') {
    return null;
  }

  if (channelKind === 'SMS') {
    const chars = bodyText.length;
    const segments = Math.max(1, Math.ceil(chars / CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.smsSegmentChars));
    return (
      <div className='space-y-2'>
        <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Preview</p>
        {loading ? <p className='text-sm text-muted-foreground'>Generando vista previa…</p> : null}
        <div className='max-w-sm rounded-2xl bg-muted px-4 py-3 text-sm whitespace-pre-wrap'>
          {preview?.bodyText || ' '}
        </div>
        <p className='text-xs text-muted-foreground'>
          {chars}/{CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.smsBodyMax} · {segments} SMS (tramos de{' '}
          {CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.smsSegmentChars})
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      {loading ? <p className='text-sm text-muted-foreground'>Generando vista previa…</p> : null}
      {preview ? (
        <RenderedEmailPreviewFrame
          subject={preview.subject}
          bodyHtml={preview.bodyHtml}
          bodyText={preview.bodyText}
        />
      ) : (
        <p className='text-sm text-muted-foreground'>Escribí el cuerpo para ver la vista previa.</p>
      )}
    </div>
  );
}
